import React from 'react'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Checkbox, Form, Popover, Select, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/lib/table'
import { useSelector } from 'react-redux'
import { getMunicipality } from '../../../store/beslutsstod/municipalitySlice'
import { ExtraTask } from '../types/municipalityData'
import { JSONLoadError } from '../utils/jsonLoader'

const { Option } = Select
const { Text } = Typography

type Props = {
  tasks: ExtraTask[]
  loadingErrors?: JSONLoadError[]
}

// "-" bär insatsbeskrivningen och visas utan prefix överst. Resten är nivåer, i
// samma ordning som nivåväljaren.
const selectionsDescription = (task: ExtraTask) => {
  const taskDescription = task.selectionDescriptions['-' as any]
  const levels = task.selections.filter((level) => level && level !== '-' && task.selectionDescriptions[level as any])

  return (
    <>
      {taskDescription && <div style={{ whiteSpace: 'pre-line', marginBottom: levels.length ? 12 : 0 }}>{taskDescription}</div>}
      {levels.map((level) => (
        <div key={level} style={{ whiteSpace: 'pre-line', marginBottom: 2 }}>
          <Text strong>{level}: </Text>
          {task.selectionDescriptions[level as any]}
        </div>
      ))}
    </>
  )
}

const ExtraTaskTable: React.FC<Props> = ({ tasks, loadingErrors }) => {
  const municipalityData = useSelector(getMunicipality)

  const showDoubleColumn = tasks.some((t) => t.double)
  const showDescription = municipalityData?.input.formSettings.showDescription

  const hasLoadingError = (taskName: string, errorType: 'frequency' | 'selection') => !!loadingErrors?.some((e) => e.taskName === taskName && e.taskType === 'other' && e.errorType === errorType)

  const columns: ColumnsType<ExtraTask> = [
    {
      title: 'Insats',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (_, task) => <Text>{task.name}</Text>,
    },
    {
      title: 'Besök per vecka',
      key: 'besok',
      onCell: (task) => ({ className: hasLoadingError(task.name, 'frequency') ? 'beslutsstod-cell-error' : undefined }),
      width: showDoubleColumn ? '25%' : '35%',
      render: (_, task) => {
        if (!task.frequencies?.length) return null
        return (
          <Form.Item name={['other', task.name, 'Frekvens']} noStyle>
            <Select
              bordered={false}
              placeholder="-"
              allowClear
              listHeight={400}
              style={{ width: '100%' }}
            >
              {task.frequencies.map((freq) => (
                <Option key={freq.name} value={freq.timesPerWeek}>
                  {freq.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )
      },
    },
    {
      title: 'Nivå',
      key: 'niva',
      onCell: (task) => ({ className: hasLoadingError(task.name, 'selection') ? 'beslutsstod-cell-error' : undefined }),
      width: showDoubleColumn ? '25%' : '35%',
      render: (_, task) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Form.Item name={['other', task.name, 'Nivå']} noStyle>
            <Select
              bordered={false}
              placeholder="-"
              allowClear
              listHeight={400}
              style={{ width: '100%' }}
            >
              {/* "-" och namnlösa nivåer är inte val, de bär insatsbeskrivningen. Rensa med krysset. */}
              {task.selections
                .filter((sel: string) => sel && sel !== '-')
                .map((sel: string) => (
                  <Option value={sel} key={sel}>
                    {sel}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          {showDescription && (
            <Popover overlayClassName="decision-popover" overlayStyle={{ width: 500 }} placement="topRight" title={task.description} content={selectionsDescription(task)} className="ml-1">
              <InfoCircleOutlined />
            </Popover>
          )}
        </div>
      ),
    },
  ]

  if (showDoubleColumn) {
    columns.push({
      title: 'Dubbelbemanning',
      key: 'dubbel',
      width: '20%',
      render: (_, task) => {
        if (!task.double) return null
        return (
          <Form.Item name={['other', task.name, 'Dubbelbemanning']} valuePropName="checked" noStyle>
            <Checkbox />
          </Form.Item>
        )
      },
    })
  }

  return <Table<ExtraTask> className="beslutsstod-task-table" size="small" bordered columns={columns} dataSource={tasks} rowKey="name" pagination={false} />
}

export default ExtraTaskTable
